import { useState, useEffect } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { userDataService } from '../services/UserDataService';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppLoad } from '../contexts/AppLoadContext';

/**
 * Hook for managing continue watching data from UserDataService
 * @returns {Object} Continue watching state and methods
 */
export const useContinueWatching = () => {
  const [continueWatchingData, setContinueWatchingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentProfile } = useProfile();
  const { isAuthenticated, user } = useAuth0();
  const { categories } = useAppLoad(); // Get categories from AppLoadContext

  useEffect(() => {
    const fetchContinueWatchingData = async () => {
      // Wait for categories to be available before processing
      if (!categories || categories.length === 0) {
        console.log('🔍 useContinueWatching: Waiting for categories to load...');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);

        if (!isAuthenticated || !currentProfile) {
          setContinueWatchingData([]);
          return;
        }

        // Fetch fresh resumption data from API for current profile and update internal cache
        await userDataService.refreshResumptionsData();

        // Get enhanced continue watching data that includes series with completed episodes
        const continueWatchingItems = await userDataService.getEnhancedContinueWatchingData();

        if (!continueWatchingItems || continueWatchingItems.length === 0) {
          setContinueWatchingData([]);
          setLoading(false);
          return;
        }

        // Filter to show only the latest episode per series
        const latestEpisodesPerSeries =
          userDataService.getLatestEpisodesPerSeries(continueWatchingItems);

        // Transform the resumption data into a format compatible with CategoryRail
        const transformedData = latestEpisodesPerSeries.map(item => {
          // Create a channel-like object from resumption data
          const channel = {
            // Use the real series ID for navigation, not the mock resumption ID
            _id: item.parent_channel?._id || item.channel?._id || item._id,
            title: item.title,
            // Use parent channel description for series, channel description for movies
            description:
              item.parent_channel?.description ||
              item.channel?.description ||
              item.description ||
              '',
            // Use the correct slug from parent channel or channel, not the resumption ID
            slug: item.parent_channel?.slug || item.channel?.slug || item.slug,
            // Use parent_channel images for series, channel images for individual content
            spotlight_poster:
              item.parent_channel?.spotlight_poster || item.channel?.spotlight_poster,
            poster: item.parent_channel?.poster || item.channel?.poster,
            thumb: item.thumb,
            video_id: item.video_id,
            channel_type: item.channel?.programming_type || 'video',
            programming_type:
              item.parent_channel?.programming_type || item.channel?.programming_type || 'Movie',
            rating: item.rating,
            // Add resumption-specific data with proper episode metadata
            resumption_data: {
              point: item.point,
              progress: item.progress,
              duration: item.duration, // Include duration for proper progress calculation
              updatedAt: item.updatedAt,
              parent_channel: item.parent_channel,
              channel: item.channel,
              // Extract episode and season numbers properly from the enhanced data
              episodeNumber: item.episode || item.channel?.episode_number || 1,
              seasonNumber: item.season || item.channel?.season_number || 1,
            },
          };

          // If this is a series episode, use parent channel data
          if (item.parent_channel) {
            channel.parent_channel_id = item.parent_channel._id;
            channel.parent_channel_title = item.seriestitle || item.parent_channel.title; // Use seriestitle from API
            channel.episode_title = item.title;
            channel.channel_type = 'series';
            channel.programming_type = 'Series';
          }

          // Look up the actual DS Pro ID from AppLoad categories
          let actualDsproId = null;
          const spotlightId = item.parent_channel?._id || item.channel?._id;
          
          console.log('🔍 useContinueWatching: Looking up dspro_id for spotlight ID:', spotlightId);
          
          if (spotlightId && categories && categories.length > 0) {
            let found = false;
            for (const category of categories) {
              if (found) {break;}
              if (category.channels && Array.isArray(category.channels)) {
                for (const cachedChannel of category.channels) {
                  if (cachedChannel._id === spotlightId && cachedChannel.dspro_id) {
                    actualDsproId = cachedChannel.dspro_id;
                    console.log('🔍 useContinueWatching: Found dspro_id:', actualDsproId, 'for spotlight ID:', spotlightId);
                    found = true;
                    break;
                  }
                }
              }
            }
            if (!found) {
              console.warn('🔍 useContinueWatching: Could not find dspro_id for spotlight ID:', spotlightId, 'in', categories.length, 'categories');
            }
          } else {
            console.warn('🔍 useContinueWatching: No categories available for dspro_id lookup');
          }

          // Set the DS Pro ID for reactions API
          channel.dspro_id = actualDsproId || spotlightId; // Use actual DS Pro ID or fallback to Spotlight ID
          channel.contentId = actualDsproId || spotlightId; // Same for watchlist operations
          
          console.log('🔍 useContinueWatching: Final IDs - dspro_id:', channel.dspro_id, 'contentId:', channel.contentId);
          

          return channel;
        });

        setContinueWatchingData(transformedData);
      } catch (err) {
        console.error('Error fetching continue watching data:', err);
        setError(err.message);
        setContinueWatchingData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContinueWatchingData();
  }, [isAuthenticated, currentProfile, categories]);

  return {
    continueWatchingData,
    loading,
    error,
    hasContinueWatching: continueWatchingData.length > 0,
  };
};
